<template>
    <div>

        <div class="row align-items-end mb-3">

            <div class="col-lg-4">
                <label for="po-stage">
                    <strong>
                        Stage
                    </strong>
                </label>
                <select v-model="filters.stage" id="po-stage" class="form-select">
                    <option :value="null">All stages</option>
                    <option value="not-chased">Not chased yet</option>
                    <option value="awaiting-reply">Chased, awaiting reply</option>
                    <option value="final-reminder-sent">Final reminder sent, running out</option>
                </select>
            </div>

            <!-- Two columns wide: the note under the box explains what unchecking gets you,
                 and at a third of the row it wraps into a bunched-up block nobody reads. -->
            <div class="col-lg-8">
                <div class="checkbox checkbox-primary">
                    <label for="po-actionable-only" title="Hide jobs chased too recently to chase again">
                        <input
                            v-model="filters.actionableOnly"
                            class="me-1"
                            id="po-actionable-only"
                            type="checkbox"
                            :disabled="showingRunningOut"
                        />
                        View actionable only
                    </label>
                </div>
                <!-- Stated as what unchecking gets you. "Include jobs in cool-down" asks the
                     operator to reason about a double negative to work out what they are
                     currently looking at. -->
                <p v-if="showingRunningOut" class="text-muted small mb-0">
                    Every job at this stage is inside its cool-down by definition, so this view
                    always includes them — they are shown for review and cannot be actioned.
                </p>
                <p v-else class="text-muted small mb-0">
                    Unchecking also lists jobs that have been chased too recently to chase again.
                    They are shown for review and cannot be actioned.
                </p>
            </div>

        </div>

        <!-- Counted over every blocked job, not over the rows on screen: the default view
             hides cooling jobs, so a summary of what is visible would report nothing waiting
             at the exact moment that is the only useful thing left to say. -->
        <div v-if="summary" class="alert alert-light border d-flex flex-wrap gap-3 mb-3">
            <span>
                <strong>{{ summary.not_chased }}</strong> not chased
            </span>
            <span>
                <strong>{{ summary.awaiting_reply }}</strong> awaiting reply
            </span>
            <!-- Amber, not red: nothing here is going wrong, and nothing can be done about
                 these rows on this screen either. They are customers running out of chances,
                 and the operator's last opportunity to get a purchase order out of them by
                 some other means before the job becomes somebody else's. A permanent red
                 zero trains the eye to ignore the strip. -->
            <span :class="{ 'text-warning fw-bold': summary.moving_to_invoicing > 0 }">
                <strong>{{ summary.moving_to_invoicing }}</strong> moving to Invoicing Manager
            </span>
            <span v-if="summary.next_actionable_at" class="text-muted ms-auto">
                Next job chaseable at {{ formatDate(summary.next_actionable_at, 'd LLL yyyy, HH:mm') }}
            </span>
            <!-- Kept apart from the chase hint on purpose: for a customer with no reminders
                 left, the clock ends in the job leaving rather than in another email, and one
                 label over both times would promise a chase that never comes. -->
            <span
                v-if="summary.next_crossover_at"
                class="text-muted"
                :class="{ 'ms-auto': !summary.next_actionable_at }"
            >
                Next job moves on at {{ formatDate(summary.next_crossover_at, 'd LLL yyyy, HH:mm') }}
            </span>
        </div>

        <div>
            <table id="datatable-po-list" ref="poTable" class="table table-bordered table-striped table-vcenter dataTable" style="width: 100%;">
                <thead>
                    <tr>
                        <!-- Cooling rows render no checkbox at all, so "select all" can only
                             ever select actionable work — there is nothing to skip. -->
                        <th class="text-center" style="width: 50px;">
                            <input type="checkbox" id="po-check-all" title="Select every actionable job on this page" />
                        </th>
                        <th>Job Ref.</th>
                        <th>Customer</th>
                        <th>Delivery Date</th>
                        <th>Journey</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody></tbody>
            </table>
        </div>

    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { bindSelectAll } from '@/helpers/bulk-select';
import { useMovementFormatting } from '@/composables/useMovementFormatting';
import { usePoActions } from '@/composables/usePoActions';
import { usePoReference } from '@/composables/usePoReference';

const props = defineProps({
    ajaxUrl: {
        type: String,
        required: true,
    },
});

const columns = [
    {
        data: 'id',
        orderable: false,
        searchable: false,
        width: '50px',
        render: (data, type, row) => row.computed_select,
    },
    {
        data: 'id',
        render: (data, type, row) => row.computed_reference,
    },
    {
        data: 'id',
        render: (data, type, row) => row.computed_customer,
    },
    {
        data: 'id',
        width: '160px',
        render: (data, type, row) => row.computed_delivery_date,
    },
    {
        data: 'id',
        width: '220px',
        render: (data, type, row) => row.computed_journey,
    },
    {
        data: 'id',
        width: '220px',
        render: (data, type, row) => row.computed_action,
    },
];

const filters = reactive({
    actionableOnly: true,
    stage: null,
});

/**
 * The running-out stage and "View actionable only" are opposites: every job at
 * that stage is inside its cool-down, so the two together match nothing. The
 * endpoint resolves it in the stage's favour, and the box is disabled here so
 * the screen says so rather than appearing to be set to something it is not.
 */
const showingRunningOut = computed(() => filters.stage === 'final-reminder-sent');

const poTable = ref(null);
const summary = ref(null);
const { formatDate } = useMovementFormatting();

// Shared with the invoice wizard's pick-jobs step: both screens offer the same
// two actions against the same endpoint, so the confirmation wording lives in
// one place rather than being paraphrased twice.
const { confirmThenProcess } = usePoActions();
const { confirmThenSetReference } = usePoReference();

onMounted(() => {

    const table = window.jQuery(poTable.value).on('preXhr.dt', (e, settings, data) => {
        // Sent explicitly rather than as a bare boolean: the endpoint defaults an
        // absent value to actionable-only, so an unchecked box has to arrive as a
        // value rather than as nothing. The stage is omitted when unset instead of
        // sent empty, which is not one of the stages the endpoint accepts.
        data.filters = {
            actionableOnly: filters.actionableOnly ? 1 : 0,
            ...(filters.stage ? { stage: filters.stage } : {}),
        };
    }).DataTable({
        processing: true,
        serverSide: true,
        pageLength: 25,
        lengthMenu: [25, 50, 100],
        ajax: {
            url: props.ajaxUrl,
        },
        // The order is the screen: longest-waiting first, never-chased at the top.
        // Sorting is off rather than left to the headers because the server owns
        // that order — a header click would append to it and appear to do nothing.
        ordering: false,
        order: [],
        columns,
    });

    table.on('xhr.dt', (e, settings, json) => {
        summary.value = json?.summary ?? null;
    });

    watch(
        filters,
        () => table.ajax.reload(null, false),
        {
            deep: true,
        }
    );

    // Select-all is per page, and only over the rows currently drawn: the table
    // is server-side, so anything on another page is not in the DOM and could
    // not be submitted.
    bindSelectAll({
        tableSelector: '#datatable-po-list',
        masterSelector: '#po-check-all',
    });

    // Delegated, on the table body: DataTables rebuilds every row on every draw,
    // so a handler bound to the buttons themselves would survive exactly one page.
    window.jQuery(table.table().body()).on('click', '.po-action-button', function () {
        const payload = JSON.parse(this.getAttribute('data-po-action'));

        confirmThenProcess(payload, () => table.ajax.reload(null, false));
    });

    window.jQuery(table.table().body()).on('click', '.po-set-reference-button', function () {
        const payload = JSON.parse(this.getAttribute('data-po-action'));

        confirmThenSetReference(payload, () => table.ajax.reload(null, false));
    });
});
</script>
